import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  BoltIcon,
  FireIcon
} from '@heroicons/react/24/solid';

/**
 * Callout Block Component
 * For important notes, warnings, tips, enterprise insights, etc.
 */
const CalloutBlock = ({ type = 'info', title, children }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const calloutTypes = {
    info: {
      icon: InformationCircleIcon,
      border: 'border-primary',
      bg: isDark ? 'bg-primary/10' : 'bg-primary/5',
      iconColor: 'text-primary',
      titleColor: 'text-primary dark:text-primary',
      emoji: 'ℹ️'
    },
    warning: {
      icon: ExclamationTriangleIcon,
      border: 'border-amber-500',
      bg: isDark ? 'bg-amber-900/10' : 'bg-amber-50/50',
      iconColor: 'text-amber-500',
      titleColor: 'text-amber-600 dark:text-amber-400',
      emoji: '⚠️'
    },
    tip: {
      icon: LightBulbIcon,
      border: 'border-emerald-500',
      bg: isDark ? 'bg-emerald-900/10' : 'bg-emerald-50/50',
      iconColor: 'text-emerald-500',
      titleColor: 'text-emerald-600 dark:text-emerald-400',
      emoji: '💡'
    },
    enterprise: {
      icon: ShieldCheckIcon,
      border: 'border-primary',
      bg: isDark ? 'bg-primary/10' : 'bg-primary/5',
      iconColor: 'text-primary',
      titleColor: 'text-primary dark:text-primary',
      emoji: '🏢'
    },
    interview: {
      icon: RocketLaunchIcon,
      border: 'border-orange-500',
      bg: isDark ? 'bg-orange-900/10' : 'bg-orange-50/50',
      iconColor: 'text-orange-500',
      titleColor: 'text-orange-600 dark:text-orange-400',
      emoji: '🎤'
    },
    performance: {
      icon: BoltIcon,
      border: 'border-purple-500',
      bg: isDark ? 'bg-purple-900/10' : 'bg-purple-50/50',
      iconColor: 'text-purple-500',
      titleColor: 'text-purple-600 dark:text-purple-400',
      emoji: '⚡'
    },
    security: {
      icon: ShieldCheckIcon,
      border: 'border-rose-500',
      bg: isDark ? 'bg-rose-900/10' : 'bg-rose-50/50',
      iconColor: 'text-rose-500',
      titleColor: 'text-rose-600 dark:text-rose-400',
      emoji: '🔒'
    },
    production_story: {
      icon: FireIcon,
      border: 'border-rose-500',
      bg: isDark ? 'bg-rose-900/10' : 'bg-rose-50/50',
      iconColor: 'text-rose-500',
      titleColor: 'text-rose-600 dark:text-rose-400',
      emoji: '💥'
    },
    decision_matrix: {
      icon: InformationCircleIcon,
      border: 'border-cyan-500',
      bg: isDark ? 'bg-cyan-900/10' : 'bg-cyan-50/50',
      iconColor: 'text-cyan-500',
      titleColor: 'text-cyan-600 dark:text-cyan-400',
      emoji: '📊'
    },
    mental_model: {
      icon: LightBulbIcon,
      border: 'border-violet-500',
      bg: isDark ? 'bg-violet-900/10' : 'bg-violet-50/50',
      iconColor: 'text-violet-500',
      titleColor: 'text-violet-600 dark:text-violet-400',
      emoji: '🧠'
    }
  };

  const style = calloutTypes[type] || calloutTypes.info;

  return (
    <div
      className={`
        ${style.bg}
        ${style.border}
        border-l-4
        rounded-2xl
        p-6
        my-10
        transition-all
        duration-300
        border-r border-t border-b ${isDark ? 'border-white/5' : 'border-slate-100'}
        shadow-sm
      `}
    >
      <div className="flex gap-5">
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-100'} flex items-center justify-center`}>
          <span className="text-xl">{style.emoji}</span>
        </div>
        <div className="flex-1">
          {title && (
            <h4 className={`font-bold text-[13px] mb-2 ${style.titleColor}`}>
              {title}
            </h4>
          )}
          <div className={`${isDark ? 'text-gray-300' : 'text-slate-600'} text-[15px] leading-relaxed font-medium`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalloutBlock;
